import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { IconAlertCircle, IconChevronDown } from "@tabler/icons-react";

interface SelectInputProps {
  label?: string;
  error?: string;
  options: { label: string; value: string }[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SelectInput: React.FC<SelectInputProps> = ({
  label,
  error,
  options,
  value,
  onChange,
  placeholder = "Select or type...",
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filtered, setFiltered] = useState(options);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    onChange(input);
    setFiltered(
      options.filter((opt) =>
        opt.label.toLowerCase().includes(input.toLowerCase())
      )
    );
    setShowDropdown(true);
  };

  const handleSelect = (val: string) => {
    onChange(val);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      {label && (
        <label className="text-sm text-muted font-medium block mb-1">
          {label}
        </label>
      )}
      <div
        className={cn(
          "flex items-center px-4 py-3 rounded-md border bg-input-bgcolor transition-all",
          error
            ? "border-red-500/80 focus-within:border-red-500/80"
            : "border-primaryborder focus-within:border-white/60"
        )}
      >
        <input
          value={value || ""}
          onChange={handleChange}
          placeholder={placeholder}
          className="w-full bg-transparent text-white text-sm outline-none placeholder:text-muted"
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)} // slight delay to capture click
        />
        {error ? (
          <div className="text-red-500/80 ml-2">
            <IconAlertCircle size={18} />
          </div>
        ) : (
          <div className="text-offwhite ml-2">
            <IconChevronDown size={18} />
          </div>
        )}
      </div>

      {showDropdown && filtered.length > 0 && (
        <ul className="absolute z-50 w-full bg-background mt-1 rounded-lg border border-primaryborder shadow-md max-h-48 overflow-y-auto">
          {filtered.map((opt) => (
            <li
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className="px-4 py-2 text-offwhite text-sm cursor-pointer hover:bg-white/10 transition-all"
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-red-500/80 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default SelectInput;
